import { Button, Chip } from '@viaja/design-system';
import { addDays, formatAdults, todayInSaoPaulo } from '@viaja/domain';
import { useEffect, useState } from 'react';
import { Sheet } from '../../ui/Sheet';
import { TextField } from '../../ui/TextField';

export interface DatesValue {
  outboundDate: string;
  returnDate: string | null;
  adults: number;
}

/** Datas, tipo de viagem e passageiros. Abre a partir do campo "Quando". */
export function DatesSheet({
  open,
  value,
  onConfirm,
  onClose,
}: {
  open: boolean;
  value: DatesValue;
  onConfirm: (v: DatesValue) => void;
  onClose: () => void;
}) {
  const tomorrow = addDays(todayInSaoPaulo(), 1);
  const [roundTrip, setRoundTrip] = useState(true);
  const [outbound, setOutbound] = useState('');
  const [ret, setRet] = useState('');
  const [adults, setAdults] = useState(1);
  const [error, setError] = useState<string | undefined>();

  // Recarrega o rascunho só quando a folha abre; mudanças em `value` com ela aberta não atropelam a edição.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intencional, ver comentário acima.
  useEffect(() => {
    if (!open) return;
    setRoundTrip(value.outboundDate ? value.returnDate != null : true);
    setOutbound(value.outboundDate);
    setRet(value.returnDate ?? '');
    setAdults(value.adults);
    setError(undefined);
  }, [open]);

  const confirm = () => {
    if (!outbound) return setError('Escolha a data de ida.');
    if (outbound < tomorrow) return setError('A ida precisa ser a partir de amanhã.');
    if (roundTrip && !ret) return setError('Escolha a data de volta.');
    if (roundTrip && ret < outbound) return setError('A volta precisa ser depois da ida.');
    onConfirm({ outboundDate: outbound, returnDate: roundTrip ? ret : null, adults });
  };

  return (
    <Sheet open={open} title="Quando" onClose={onClose}>
      <fieldset className="app-fieldset app-wrap">
        <legend className="app-visually-hidden">Tipo de viagem</legend>
        <Chip selected={roundTrip} onClick={() => setRoundTrip(true)}>
          Ida e volta
        </Chip>
        <Chip selected={!roundTrip} onClick={() => setRoundTrip(false)}>
          Só ida
        </Chip>
      </fieldset>
      <TextField
        label="Ida"
        type="date"
        min={tomorrow}
        value={outbound}
        onChange={(e) => {
          setOutbound(e.target.value);
          if (ret && e.target.value > ret) setRet('');
        }}
        error={error && !outbound ? error : undefined}
      />
      {roundTrip ? (
        <TextField
          label="Volta"
          type="date"
          min={outbound || tomorrow}
          value={ret}
          onChange={(e) => setRet(e.target.value)}
        />
      ) : null}
      <div className="app-row">
        <span className="body-strong">Passageiros</span>
        <div className="app-stepper">
          <button
            type="button"
            aria-label="Menos um adulto"
            disabled={adults <= 1}
            onClick={() => setAdults((a) => Math.max(1, a - 1))}
          >
            −
          </button>
          <span className="body vc-num" aria-live="polite">
            {formatAdults(adults)}
          </span>
          <button
            type="button"
            aria-label="Mais um adulto"
            disabled={adults >= 9}
            onClick={() => setAdults((a) => Math.min(9, a + 1))}
          >
            +
          </button>
        </div>
      </div>
      {error && outbound ? (
        <span className="caption app-field-error" role="alert">
          {error}
        </span>
      ) : null}
      <Button block onClick={confirm}>
        Confirmar datas
      </Button>
    </Sheet>
  );
}
